import { FC, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon, BarbellIcon, SoccerBallIcon } from '@phosphor-icons/react';
import { AthleteExerciseHistory } from './AthleteExerciseHistory';
import { RtpMonitoringDashboard } from './RtpMonitoringDashboard';
import AssignWorkoutModal from '../../../components/ProgrammingPage/AssignWorkoutModal/AssignWorkoutModal';
import FieldWorkoutBuilderModal from '../../../components/ProgrammingPage/FieldWorkoutBuilderModal/FieldWorkoutBuilderModal';
import WorkoutPlanActivitySection from '../../../components/ProgrammingPage/WorkoutPlanActivitySection/WorkoutPlanActivitySection';
import { AthleteFieldWorkoutsSection } from './AthleteFieldWorkoutsSection';
import { WORKOUT_CATEGORY } from '../../../enums/programming';
import styles from './ProgrammingTab.module.scss';

type Mode = 'gym' | 'field';
type GymSubSection = 'attivita' | 'exercises' | 'rtp';

interface ProgrammingTabProps {
  athleteId: string;
}

const GYM_SUB_SECTION_LABELS: Record<GymSubSection, string> = {
  attivita: 'Attività',
  exercises: 'Storico esercizi',
  rtp: 'Monitoraggio RTP',
};

export const ProgrammingTab: FC<ProgrammingTabProps> = ({ athleteId }) => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('gym');
  const [activeGymSection, setActiveGymSection] = useState<GymSubSection>('attivita');
  const [gymAssignOpen, setGymAssignOpen] = useState(false);
  const [fieldBuilderOpen, setFieldBuilderOpen] = useState(false);

  const handleGymAssignClose = () => {
    setGymAssignOpen(false);
    queryClient.invalidateQueries({ queryKey: ['programming-assignments'] });
  };

  const handleFieldBuilderClose = () => {
    setFieldBuilderOpen(false);
    queryClient.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] });
    queryClient.invalidateQueries({ queryKey: ['athleteFieldWorkouts', athleteId] });
  };

  return (
    <div className={styles.wrapper}>
      {/* Macro toggle Palestra / Campo */}
      <div className={styles.modeSwitcher}>
        <button
          type="button"
          onClick={() => setMode('gym')}
          className={`${styles.modeBtn} ${mode === 'gym' ? styles.modeBtnActive : ''}`}
        >
          <BarbellIcon size={16} weight="fill" />
          Programmazione Palestra
        </button>
        <button
          type="button"
          onClick={() => setMode('field')}
          className={`${styles.modeBtn} ${mode === 'field' ? styles.modeBtnActive : ''}`}
        >
          <SoccerBallIcon size={16} weight="fill" />
          Programmazione Campo
        </button>
      </div>

      {mode === 'gym' && (
        <>
          <div className={styles.navBar}>
            <div className={styles.navGroup}>
              {(Object.keys(GYM_SUB_SECTION_LABELS) as GymSubSection[]).map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveGymSection(section)}
                  className={[styles.navBtn, activeGymSection === section ? styles.navBtnActive : ''].filter(Boolean).join(' ')}
                >
                  {GYM_SUB_SECTION_LABELS[section]}
                </button>
              ))}
            </div>

            <button
              onClick={() => setGymAssignOpen(true)}
              className={styles.assignBtn}
            >
              <PlusIcon size={14} weight="bold" />
              Assegna scheda Palestra
            </button>
          </div>

          {activeGymSection === 'attivita' && <WorkoutPlanActivitySection athleteId={athleteId} />}
          {activeGymSection === 'exercises' && <AthleteExerciseHistory athleteId={athleteId} />}
          {activeGymSection === 'rtp' && <RtpMonitoringDashboard athleteId={athleteId} />}
        </>
      )}

      {mode === 'field' && (
        <>
          <div className={styles.navBar}>
            <div className={styles.navGroupHint}>
              Schede Campo personalizzate assegnate a questo atleta
            </div>
            <button
              onClick={() => setFieldBuilderOpen(true)}
              className={styles.assignBtn}
            >
              <PlusIcon size={14} weight="bold" />
              Nuova scheda Campo
            </button>
          </div>

          <AthleteFieldWorkoutsSection athleteId={athleteId} />
        </>
      )}

      <AssignWorkoutModal
        isOpen={gymAssignOpen}
        onClose={handleGymAssignClose}
        workout={null}
        preSelectedAthleteIds={athleteId ? [athleteId] : undefined}
        initialCategory={activeGymSection === 'rtp' ? WORKOUT_CATEGORY.RTP_REHAB : undefined}
      />

      <FieldWorkoutBuilderModal
        isOpen={fieldBuilderOpen}
        workout={null}
        onClose={handleFieldBuilderClose}
        preSelectedAthleteId={athleteId}
      />
    </div>
  );
};

export default ProgrammingTab;
